import { useState } from "react";
import { AlertTriangle, Info, ChevronDown, ChevronRight } from "lucide-react";
import { humanize, assumabilityColor, workabilityColor } from "@/lib/perDocLabels";

// Tooltip descriptors for the 5 B.1 breakdown areas.
const AREA_TOOLTIPS = {
  support: "Qualità materiale e accessibilità del documento in verifica.",
  quantity: "Numero e distribuzione delle scritture comparative disponibili.",
  quality: "Originalità, attribuibilità, ufficialità, varietà grafica e corrispondenza strutturale.",
  temporal: "Distribuzione delle comparative rispetto alla data del singolo documento.",
  context_completeness: "Disponibilità delle informazioni cliniche, cronologiche, personali e processuali pertinenti.",
};

const AREA_LABELS = {
  support: "Supporto / disponibilità",
  quantity: "Quantità comparative",
  quality: "Qualità comparative",
  temporal: "Rappresentatività temporale",
  context_completeness: "Completezza del contesto",
};

const AREA_MAX = { support: 25, quantity: 20, quality: 25, temporal: 15, context_completeness: 15 };

function DocCard({ a, expanded, onToggle, allAssessments, alertLabel = "elementi da approfondire" }) {
  const idx = a.document_index ?? 0;
  const title = a.document_title || humanize(a.document_type) || `Documento #${idx.toString().padStart(2, "0")}`;
  const nAlerts = (a.alerts || []).length;
  const hasProvisional = a.service_recommendation_provisional;
  return (
    <div className="border hairline bg-white" data-testid={`multidoc-card-${idx}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left"
        data-testid={`multidoc-card-${idx}-toggle`}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-slate-500">DOC #{idx.toString().padStart(2, "0")}</span>
            <span className="text-xs uppercase tracking-wider text-slate-500">
              {humanize(a.document_role)}
            </span>
          </div>
          <div className="font-serif text-lg font-semibold text-navy leading-tight">{title}</div>
          <div className="text-xs text-slate-500 mt-1">
            {a.document_date && `${a.document_date} · `}
            {humanize(a.support_type)}
            {a.original_status && ` · ${humanize(a.original_status)}`}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="font-serif text-2xl font-semibold text-navy leading-none" data-testid={`multidoc-card-${idx}-score`}>
              {a.preparation_score}<span className="text-sm text-slate-400">/100</span>
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end min-w-[8rem]">
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${workabilityColor(a.workability)}`}>
              {a.workability}
            </span>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${assumabilityColor(a.assumability)}`}>
              {a.assumability}
            </span>
          </div>
          {expanded ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t hairline p-4 bg-slate-50 space-y-4" data-testid={`multidoc-card-${idx}-detail`}>
          {/* Servizio */}
          <div>
            <div className="overline mb-1">Servizio consigliato</div>
            <div className="font-serif text-base font-semibold">{a.next_service}</div>
            {hasProvisional && (
              <div className="mt-2 text-xs bg-amber-50 border border-amber-300 text-amber-900 p-2 rounded-sm flex gap-2">
                <Info size={14} className="shrink-0 mt-0.5" />
                <span>Indicazione provvisoria: alcuni dati necessari alla valutazione completa
                  della corrispondenza strutturale non risultano ancora disponibili.</span>
              </div>
            )}
          </div>

          {/* Breakdown */}
          <div>
            <div className="overline mb-2">Breakdown del punteggio</div>
            <div className="space-y-2">
              {Object.entries(a.score_breakdown || {}).map(([k, v]) => {
                const max = AREA_MAX[k] || 25;
                const pct = Math.min(100, (v / max) * 100);
                return (
                  <div key={k} title={AREA_TOOLTIPS[k] || ""}>
                    <div className="flex justify-between text-xs">
                      <span>{AREA_LABELS[k] || k}</span>
                      <span className="font-mono text-slate-600">{v}/{max}</span>
                    </div>
                    <div className="h-1 bg-slate-200 mt-1 relative">
                      <div className="absolute inset-y-0 left-0 bg-navy" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Corrispondenza strutturale */}
          {a.structural_correspondence && (
            <div>
              <div className="overline mb-1">Corrispondenza strutturale</div>
              <div className="font-serif text-base">
                {humanize(a.structural_correspondence.level)}
              </div>
              {a.structural_correspondence.level === "non_determinabile" && (
                <p className="text-xs italic text-slate-500 mt-1">
                  Mancano alcune informazioni sulla struttura grafica del documento.
                  Il dato non è stato considerato negativamente.
                </p>
              )}
            </div>
          )}

          {/* Temporal distribution */}
          {a.temporal_distribution && (
            <div className="text-xs text-slate-600">
              <span className="font-medium">Comparative — </span>
              coeve: {a.temporal_distribution.coeva || 0} ·{" "}
              prossime: {(a.temporal_distribution.antecedente_prossima || 0) + (a.temporal_distribution.successiva_prossima || 0)} ·{" "}
              remote: {(a.temporal_distribution.antecedente_remota || 0) + (a.temporal_distribution.successiva_remota || 0)} ·{" "}
              non det.: {a.temporal_distribution.non_determinabile || 0}
            </div>
          )}

          {/* Weaknesses + Alerts + Recommendations */}
          {(a.weaknesses || []).length > 0 && (
            <div>
              <div className="overline mb-1">Criticità (max 3)</div>
              <ul className="text-xs space-y-1">
                {(a.weaknesses || []).slice(0, 3).map((w, i) => <li key={i}>· {w}</li>)}
              </ul>
            </div>
          )}
          {nAlerts > 0 && (
            <div>
              <div className="overline mb-1">
                {nAlerts} {alertLabel}
              </div>
              <ul className="text-xs space-y-1">
                {a.alerts.slice(0, 5).map((al, i) => {
                  const sharedWith = (allAssessments || [])
                    .filter((x) => x.document_index !== idx)
                    .filter((x) => (x.alerts || []).some((oa) =>
                      oa.category === "sanitario" && oa.event_date === al.event_date && oa.event_label === al.event_label))
                    .map((x) => `DOC #${x.document_index.toString().padStart(2, "0")}`);
                  return (
                    <li key={i} className="flex gap-2 items-start">
                      <AlertTriangle size={12} className="text-forensic-amber shrink-0 mt-0.5" />
                      <span>
                        <b>{al.severity === "critico" ? "Critico" : "Attenzione"}</b> — {al.reason}
                        {sharedWith.length > 0 && (
                          <em className="text-slate-500 ml-1">
                            (rilevante anche per {sharedWith.join(", ")})
                          </em>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MultidocSummary({ assessments, title = "Valutazioni per documento", alertLabel, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(() =>
    defaultExpanded ? new Set((assessments || []).map((a) => a.document_index)) : new Set()
  );
  // Empty states (B.3.2)
  if (assessments === null || assessments === undefined) {
    return (
      <section data-testid="multidoc-summary-error" className="border-l-2 border-forensic-red bg-red-50 p-3 text-sm text-red-700">
        Non è stato possibile caricare la valutazione. Riprova.
      </section>
    );
  }
  if (assessments.length === 0) {
    return (
      <section data-testid="multidoc-summary-empty" className="border hairline bg-slate-50 p-4 text-sm text-slate-500 italic">
        Valutazione non ancora disponibile. Completa i dati necessari e aggiorna il fascicolo.
      </section>
    );
  }
  const toggle = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const nonComplete = assessments.filter((a) => a.quality_status === "non_completamente_valutabile");
  return (
    <section data-testid="multidoc-summary">
      <div className="overline mb-3">{title}</div>
      {nonComplete.length > 0 && (
        <div className="mb-3 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2 rounded-sm" data-testid="multidoc-provisional-warning">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            Alcune caratteristiche grafiche di {nonComplete.length === 1 ? "un documento" : `${nonComplete.length} documenti`} non
            risultano compilate. La valutazione del servizio potrebbe essere provvisoria.
          </span>
        </div>
      )}
      <div className="space-y-2">
        {assessments.map((a) => (
          <DocCard
            key={a.document_index}
            a={a}
            expanded={expanded.has(a.document_index)}
            onToggle={() => toggle(a.document_index)}
            allAssessments={assessments}
            alertLabel={alertLabel}
          />
        ))}
      </div>
    </section>
  );
}
